import { useMemo, useState } from "react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { EnhancedVoiceSelectorV2 } from "@/components/voice/EnhancedVoiceSelectorV2";
import { useVoice, useVoices } from "@/hooks/voice/useVoices";
import { Voice } from "@/types/voice/voice";
import { PreselectedVoice, PreselectedVoiceInput } from "@/types/voice/preselected-voice";

interface PreselectedVoiceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editingVoice: PreselectedVoice | null;
  form: PreselectedVoiceInput;
  setForm: React.Dispatch<React.SetStateAction<PreselectedVoiceInput>>;
  onSave: () => void;
  isSaving: boolean;
}

export const PreselectedVoiceDrawer = ({
  isOpen,
  onClose,
  editingVoice,
  form,
  setForm,
  onSave,
  isSaving,
}: PreselectedVoiceDrawerProps) => {
  const { data: voicesResult, isLoading: voicesLoading } = useVoices();
  const liveVoices = voicesResult?.voices ?? [];
  const { data: selectedVoiceLookup } = useVoice(form.voice_id || undefined);
  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>(undefined);

  const handleVoiceChange = (voiceId: string) => {
    setForm((current) => ({
      ...current,
      voice_id: voiceId,
    }));
  };

  const handleVoicePick = (voice: Voice) => {
    setSelectedVoice(voice);
    setForm((current) => ({
      ...current,
      voice_id: voice.id,
      label: current.label || voice.name || "",
      description: current.description ?? voice.description ?? "",
      gender: current.gender || (voice.labels?.gender === "female" ? "female" : "male"),
    }));
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={editingVoice ? "Edit Preselected Voice" : "Add Preselected Voice"}
      className="sm:!max-w-[40vw]"
    >
      <div className="flex flex-col gap-5 py-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-genie-text-primary">ElevenLabs Voice *</label>
          <EnhancedVoiceSelectorV2
            value={form.voice_id}
            onChange={handleVoiceChange}
            onVoicePick={handleVoicePick}
            voices={liveVoices}
            voicesLoading={voicesLoading}
            currentVoice={selectedVoice || selectedVoiceLookup}
            className="w-full"
            allowLibraryAdd={true}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-genie-text-primary">Label *</label>
          <Input
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="e.g., Sam"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-genie-text-primary">Description</label>
          <Input
            value={form.description ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="e.g., New Zealand, Playful, and Warm"
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-genie-text-primary">Gender *</label>
            <select
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value as "male" | "female" }))}
              className="rounded-md border border-genie-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-genie-primary"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className="flex-1 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-genie-text-primary">Sort Order</label>
            <Input
              type="number"
              value={form.sort_order ?? 0}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
            />
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <Switch
            checked={form.is_active ?? true}
            onCheckedChange={(checked) => setForm((f) => ({ ...f, is_active: checked }))}
          />
          <span className="text-sm text-genie-text-secondary">Active (visible to users)</span>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0"
          >
            {isSaving ? "Saving..." : editingVoice ? "Save Changes" : "Add Voice"}
          </Button>
        </div>
      </div>
    </CoreDrawer>
  );
};
